/**
 * Die Karte „Unterkunft" auf der Event-Seite — ein Verweis auf die Hotelsuche von Booking.com,
 * bei der Startort und Reisedaten des Events schon eingetragen sind.
 *
 * Alles, was aus dem Event *abgeleitet* wird, steht hier: Titel, Datumszeile, Suchadresse und die
 * Frage, ob die Karte überhaupt erscheint. Web und App zeichnen nur noch — dieselbe Regel wie bei
 * [eventCardModel]. Die Datumszeile zweimal zu schreiben wäre der sichere Weg in zwei Formate.
 *
 * Gerechnet wird in **lokaler** Zeit, nie über `toISOString()`: östlich von Greenwich fällt die
 * UTC-Mitternacht eines Tages auf den Vortag, und die Anreise läge dann einen Tag daneben.
 */
import { CyclingEvent } from './types';
import { MONTH_NAMES_DE, daysUntilEvent, parseLocalDay } from './utils';
import { outboundClickContext } from './trackingContext';

/**
 * Der Partnerlink aus dem Booking.com-Programm bei **CJ Affiliate** (25.09.2026).
 *
 * Es ist kein Booking-eigener Link mit `aid=`, sondern ein Klickzähler von CJ: der Besucher geht
 * über `tkqlhce.com` → `cj.dotomi.com` → `emjcd.com` und erst dann zu Booking. Erst dieser Umweg
 * setzt das Cookie, an dem eine spätere Buchung uns zugerechnet wird — **die Kette darf also nie
 * abgekürzt werden**, auch wenn ein direkter Booking-Link kürzer aussähe.
 *
 * Nachgemessen am 25.09.2026 (`curl -L`): Booking bekommt am Ende genau die Adresse, die in `url`
 * stand, und CJ hängt seine eigene Kennung an — `aid=818287` plus ein `label`, in dem unsere
 * [BOOKING_SUBID_PARAM] als `clkid-…` auftaucht. Damit ist die Zuordnung je Event dort ablesbar,
 * wo das Geld gezählt wird, und nicht nur bei uns.
 *
 * **Das Cookie hält nur die Sitzung** (Welcome Pack, „Program is session based"), und vergütet
 * werden 4 % auf *abgeschlossene Aufenthalte*. Es gibt also kein Fenster, in dem ein Besucher
 * später zurückkommt: entweder er bucht im selben Browserlauf, oder der Klick war umsonst. Genau
 * deshalb zeigt die Karte auf eine **fertige Suche** und nicht auf die Startseite — jeder Schritt,
 * den er danach noch selbst tun muss, ist einer, an dem die Vergütung verfällt.
 *
 * Es ist der **„Evergreen"-Link** aus dem CJ-Portal. Das Welcome Pack nennt ihn als einzigen, der
 * Deeplinking zugesichert unterstützt — deshalb steht er hier, obwohl am 25.09.2026 auch zwei
 * andere CJ-Links (`-13397410`, `-14082404`) die Suche vollständig durchtrugen. Was heute
 * nebenbei funktioniert, ist morgen keine Zusage.
 *
 * Die Domain ist dabei austauschbar (CJ zählt über `anrdoezrs.net`, `kqzyfj.com`, `tkqlhce.com`,
 * `jdoqocy.com`). Entscheidend ist die zweite Zahl, die Link-Id: sie steht bei Booking im `label`
 * als `affnetcj-<id>` und sagt dort, welcher CJ-Anzeige der Klick gutgeschrieben wird. Wer den
 * Link tauscht, tauscht die Zurechnung im CJ-Bericht, sonst nichts.
 */
export const BOOKING_AFFILIATE_LINK: string | null =
  'https://www.anrdoezrs.net/click-101885764-15734849';

/**
 * Der Parameter, über den CJ das **Ziel** entgegennimmt. Ohne ihn landet der Besucher auf der
 * Startseite von Booking — genau das war der Zustand, als der Link frisch aus dem Partner-Center
 * kam.
 *
 * Der Wert wird **kodiert** übergeben. Das Welcome Pack zeigt ihn roh
 * (`?url=https://www.booking.com/villas/index.en-gb.html`), aber sein Beispielziel hat keine
 * eigene Abfrage. Unseres hat vier Parameter, und ein rohes `&checkin=` läse CJ als *seinen*
 * Parameter — der Besucher käme auf einer Suche ohne Datum an.
 */
const BOOKING_TARGET_PARAM = 'url';

/**
 * Der Parameter für die eigene Unterkennung. CJ reicht ihn als `clkid-<wert>` an Booking weiter,
 * damit sich ein Abschluss einem Event zuordnen lässt. Erlaubt sind nur Buchstaben, Ziffern,
 * Binde- und Unterstrich — alles andere kommt auf der anderen Seite verstümmelt an.
 */
const BOOKING_SUBID_PARAM = 'sid';

/** Die Suchseite auf Deutsch. Unsere Besucher sind deutschsprachig, das ist keine Vermutung. */
const BOOKING_SEARCH_URL = 'https://www.booking.com/searchresults.de.html';

/** Die Startseite — der Rückfall, solange oder falls es keinen Partnerlink gibt. */
export const BOOKING_HOME_URL = 'https://www.booking.com/';

/** Kennzeichnung als Werbelink. Pflicht, und deshalb keine Entscheidung der Plattform. */
export const ACCOMMODATION_AD_LABEL = 'PARTNERLINK';

/** Beschriftung des Links. Steht hier, damit Web und App nicht zwei Fassungen bekommen. */
export const ACCOMMODATION_LINK_LABEL = 'Bei Booking.com suchen ›';

/** Das Ziel eines Klicks im Tracking (`TrackingRepository.CONTEXT_BASE`). */
export const ACCOMMODATION_CLICK_TARGET = 'unterkunft';

/**
 * Der vollständige Klick-Kontext, den Web und App melden — mit **`booking.com`** als Zieldomain,
 * nicht mit der Domain des Partnerlinks.
 *
 * Der Link zeigt technisch auf `anrdoezrs.net`, und genau die wäre die falsche Auskunft: CJ zählt
 * über vier austauschbare Domains, und schon ein Wechsel im Partner-Center ließe dieselbe
 * Auswertung in mehrere Zeilen zerfallen. Die Frage, die der Domain-Teil beantworten soll, lautet
 * „wen beliefern wir" — und das ist Booking, nicht der Zähler davor.
 */
export const ACCOMMODATION_CLICK_CONTEXT = outboundClickContext(ACCOMMODATION_CLICK_TARGET, BOOKING_HOME_URL);

export interface AccommodationCard {
  /** Der Startort, so wie er am Event steht. */
  city: string;
  /** „Übernachten in Bad Honnef" */
  title: string;
  /** „13.–14. Juni · Anreise am Vortag" */
  dateLine: string;
  /** Anreisetag, `YYYY-MM-DD` — der Tag vor dem Event. */
  checkin: string;
  /** Abreisetag, `YYYY-MM-DD` — bei mehrtägigen Events der letzte Event-Tag. */
  checkout: string;
  /** Koordinaten des Startorts, sofern am Event gepflegt — sonst `null`. */
  point: { lat: number; lon: number } | null;
  /** Die Adresse, die der Klick öffnet. */
  url: string;
}

function iso(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * „13.–14. Juni" innerhalb eines Monats, sonst „31. Mai – 1. Juni".
 *
 * Die kurze Form über den Monatswechsel hinweg („31.–1. Juni") nennt den Mai nirgends und liest
 * sich wie ein Tippfehler.
 */
function stayDates(from: Date, to: Date): string {
  const month = (d: Date) => MONTH_NAMES_DE[d.getMonth()];
  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
    return `${from.getDate()}.–${to.getDate()}. ${month(to)}`;
  }
  return `${from.getDate()}. ${month(from)} – ${to.getDate()}. ${month(to)}`;
}

/** Zeichen, die CJ und Booking unbeschadet durch die Kette tragen. */
function subId(prefix: string, id: number | string): string {
  return `${prefix}-${String(id).replace(/[^A-Za-z0-9_-]/g, '')}`;
}

/** Fünf Nachkommastellen sind gut einen Meter — alles darüber bläht nur die Adresse. */
function coord(value: number): string {
  return String(Math.round(value * 1e5) / 1e5);
}

/**
 * Die Suche bei Booking mit Ort, Reisedaten und — wenn vorhanden — den Koordinaten des Startorts.
 *
 * **Ort und Koordinaten zusammen**, nicht eines von beiden. Gemessen am 25.09.2026 an einem Start
 * rund 6 km außerhalb des Zentrums (Bad Honnef-Aegidienberg):
 *
 * - nur `ss`: Booking rechnet ab **Stadtzentrum** („Zentrum: 1,7 km"), und die Häuser direkt am
 *   Start stehen gar nicht oben. Für jemanden, der am Morgen zum Start rollen will, ist das die
 *   falsche Reihenfolge.
 * - nur Koordinaten: richtig sortiert, aber die Suche verliert ihren Namen („801 Unterkünfte sind
 *   in und um dieses Reiseziel", Suchfeld leer).
 * - **beides**: „Bad Honnef: 801 Unterkünfte gefunden", Suchfeld gefüllt, und die Liste beginnt
 *   mit 0,8 km · 1 km · 2,2 km **vom Punkt auf der Karte**.
 *
 * `order=distance_from_search` ist dabei überflüssig (Booking sortiert schon so) und `radius`
 * wirkungslos — beide bewusst weggelassen, statt sie als Zierde mitzuschleppen.
 *
 * Bewusst **ohne** `aid`: bei CJ kommt die Partnerkennung aus der Weiterleitung. Eine eigene
 * hineinzuschreiben hieße, zwei Kennungen im selben Aufruf zu haben, und welche dann zählt,
 * entscheidet nicht diese Datei.
 */
function searchUrl(card: Omit<AccommodationCard, 'url'>): string {
  const params = [
    `ss=${encodeURIComponent(card.city)}`,
    `checkin=${card.checkin}`,
    `checkout=${card.checkout}`,
    // Eine Person, ein Zimmer: die Karte ist ein Einstieg, keine Reisebuchung. Wer zu zweit
    // fährt, stellt das auf der Suchseite in einem Klick um.
    'group_adults=1',
    'no_rooms=1',
  ];
  if (card.point) {
    params.push(`latitude=${coord(card.point.lat)}`, `longitude=${coord(card.point.lon)}`);
  }
  return `${BOOKING_SEARCH_URL}?${params.join('&')}`;
}

/**
 * Die Suche, verpackt in den Klickzähler des Partnerprogramms.
 *
 * Steht im Partnerlink schon eine Abfrage, hängen wir mit `&` an statt mit `?` — sonst entstünde
 * eine zweite Abfrage in derselben Adresse und der Zähler verlöre seine eigenen Angaben.
 */
function affiliateUrl(link: string, target: string, id: number | string, prefix: string): string {
  const separator = link.indexOf('?') >= 0 ? '&' : '?';
  return `${link}${separator}${BOOKING_TARGET_PARAM}=${encodeURIComponent(target)}`
    + `&${BOOKING_SUBID_PARAM}=${subId(prefix, id)}`;
}

/**
 * Der gemeinsame Kern: aus Ort, Anreise- und Abreisetag wird die Karte. Die beiden Einstiege
 * darunter unterscheiden sich nur darin, **welche Nacht** gemeint ist — die Rechnerei, die
 * Datumszeile und der Partnerlink stehen genau einmal.
 */
function buildCard(
  input: { id: number | string; city: string; arrival: Date; departure: Date; note: string;
           point?: { lat: number; lon: number } | null },
  affiliateLink: string | null,
  sidPrefix: string
): AccommodationCard {
  const base = {
    city: input.city,
    title: `Übernachten in ${input.city}`,
    dateLine: `${stayDates(input.arrival, input.departure)} · ${input.note}`,
    checkin: iso(input.arrival),
    checkout: iso(input.departure),
    point: input.point ?? null,
  };
  const search = searchUrl(base);
  return {
    ...base,
    url: affiliateLink ? affiliateUrl(affiliateLink, search, input.id, sidPrefix) : BOOKING_HOME_URL,
  };
}

/**
 * Das Modell der Karte für ein Event, oder `null` wenn sie nicht erscheinen soll.
 *
 * Sie erscheint nicht ohne Startort, nicht für **vergangene** Events (dort gibt es nichts mehr zu
 * buchen) und nicht für **abgesagte** — für eine Fahrt, die ausfällt, ein Hotel zu vermitteln,
 * wäre der Seite nicht angemessen.
 *
 * Gebucht wird die Nacht **vor** dem Event: wer am Sonntagmorgen am Start stehen will, reist am
 * Samstag an.
 */
export function accommodationCard(
  event: Pick<CyclingEvent, 'id' | 'city' | 'eventDate'>
    & { endDate?: string | null; cancelled?: boolean; latitude?: number | null; longitude?: number | null },
  opts: { affiliateLink?: string | null } = {}
): AccommodationCard | null {
  const city = (event.city ?? '').trim();
  if (!city || !event.eventDate) return null;
  if (event.cancelled) return null;
  if (daysUntilEvent(event.eventDate, event.endDate) < 0) return null;

  const start = parseLocalDay(event.eventDate);
  const end = event.endDate ? parseLocalDay(event.endDate) : start;
  const last = end.getTime() > start.getTime() ? end : start;

  return buildCard({
    id: event.id,
    city,
    arrival: new Date(start.getFullYear(), start.getMonth(), start.getDate() - 1),
    departure: last,
    note: 'Anreise am Vortag',
    // Beide oder keine: eine halbe Koordinate zeigt in den Atlantik.
    point: typeof event.latitude === 'number' && typeof event.longitude === 'number'
      ? { lat: event.latitude, lon: event.longitude }
      : null,
  }, resolveLink(opts), 'event');
}

/**
 * Dasselbe für eine **Etappe eines Profi-Rennens**, mit zwei Unterschieden zum Event.
 *
 * **Der Zielort, nicht der Start.** Dort stehen die Zuschauer, dort ist abends Betrieb — und nach
 * einem Bergfinale fährt niemand um 18 Uhr heim.
 *
 * **Die Nacht *nach* der Etappe**, nicht die davor. Das ist die Umkehrung der Event-Regel und
 * folgt daraus: wer am Ziel zusieht, braucht das Bett hinterher. Deshalb steht die Nacht auch in
 * der Datumszeile dabei, sonst läse man die Zeile mit der Erwartung aus der Event-Karte.
 *
 * Koordinaten hat eine Etappe nicht (`pro_race_stages` führt nur Ortsnamen). Für Zuschauer wiegt
 * das leicht: sie wollen in den Ort, nicht auf 800 Meter an den Zielstrich.
 */
export function stageAccommodationCard(
  stage: { id: number | string; endCity?: string | null; stageDate?: string | null },
  opts: { affiliateLink?: string | null } = {}
): AccommodationCard | null {
  const city = (stage.endCity ?? '').trim();
  if (!city || !stage.stageDate) return null;
  if (daysUntilEvent(stage.stageDate) < 0) return null;

  const day = parseLocalDay(stage.stageDate);
  return buildCard({
    id: stage.id,
    city,
    arrival: day,
    departure: new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1),
    note: 'Nacht nach der Etappe',
  }, resolveLink(opts), 'stage');
}

/**
 * Die Karte auf der **Rennseite** eines Profi-Rennens — nur für Eintagesrennen.
 *
 * Grund für die Einschränkung: eine Rundfahrt hat keinen Ort, den man buchen könnte. Drei Wochen,
 * einundzwanzig Zielorte — die Rennseite kann darauf keine Antwort geben, die Etappenseite schon.
 * Ein Eintagesrennen dagegen *ist* sein Ort und sein Tag, und dort ist die Rennseite die Seite,
 * auf der die Leute stehen; seine Etappenseite kennt kaum jemand.
 *
 * Erkannt wird das am **Zeitraum, nicht am Etikett**: `startDate === endDate`. `raceType` ist ein
 * gepflegter Text und kann fehlen oder danebenliegen, die beiden Daten stehen immer.
 *
 * Der Ort kommt aus der einzigen Etappe — `pro_races` selbst führt nur ein Land, und
 * „Deutschland" ist keine Hotelsuche. Ein Eintagesrennen ohne Etappenzeile bekommt deshalb keine
 * Karte; in Prod betrifft das eines von acht.
 */
export function proRaceAccommodationCard(
  race: {
    id: number | string;
    startDate: string;
    endDate?: string | null;
    stages?: Array<{ id: number | string; startCity?: string | null; endCity?: string | null }>;
  },
  opts: { affiliateLink?: string | null } = {}
): AccommodationCard | null {
  if (!race.startDate || !race.endDate || race.startDate !== race.endDate) return null;
  if (daysUntilEvent(race.startDate) < 0) return null;

  const stage = (race.stages ?? [])[0];
  // Ziel vor Start: bei einem Eintagesrennen meist derselbe Ort, sonst gilt dieselbe Überlegung
  // wie bei der Etappe — gebucht wird, wo das Rennen endet.
  const city = ((stage?.endCity ?? stage?.startCity) ?? '').trim();
  if (!stage || !city) return null;

  const day = parseLocalDay(race.startDate);
  return buildCard({
    // Gezählt wird auf der Etappe, aus der der Ort stammt: dieselbe Übernachtung, egal über
    // welche der beiden Seiten sie gefunden wurde.
    id: stage.id,
    city,
    arrival: day,
    departure: new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1),
    note: 'Nacht nach dem Rennen',
  }, resolveLink(opts), 'stage');
}

/** `undefined` heißt „nimm den hinterlegten Link", `null` heißt ausdrücklich „keiner". */
function resolveLink(opts: { affiliateLink?: string | null }): string | null {
  return opts.affiliateLink === undefined ? BOOKING_AFFILIATE_LINK : opts.affiliateLink;
}
